import CustomTable from "@/components/CustomTable/CustomTable.vue";
import CustomTabs from "@/components/CustomTab/CustomTab.vue";
import AdminNavBar from "@/components/AdminNavBar/AdminNavBar.vue";
import { API_BASE_URL } from "@/config";
import { validateToken } from "@/utils/auth";
import { format_date } from '@/utils/date_formatter';
import { useToast } from "@/components/Toast/useToast";

const STATUS_MAP = {
  pending: "pending",
  approved: "approved",
  declined: "declined",
  incomplete: "incomplete",
};

export default {
  name: "ViewWorkflowStatus",
  components: {
    AdminNavBar,
    CustomTable,
    CustomTabs,
  },
  data() {
    return {
      currentTab: localStorage.getItem("workflow_tabs") || "pending",
      columns: [
        { label: "Name", key: "workflow_name" },
        { label: "Created By", key: "created_by" },
        { label: "Date Created", key: "date_created" },
        { label: "Workflow Status", key: "is_active" },
      ],
      tabs: [
        { label: "Pending", value: "pending" },
        { label: "Approved", value: "approved" },
        { label: "Declined", value: "declined" },
        { label: "Incomplete", value: "incomplete" },
      ],
      // Import Workflow properties
      projects: [],
      loadingProjects: false,
      selectedProjectId: null,
      jsonText: '',
      parsedWorkflow: null,
      validationError: '',
      importing: false,
      importSuccess: '',
      servicesNotFound: [],
      workflowsTableKey: 0,
      isDragging: false,
      uploadedFile: null,
      uploadProgress: 100,
      currentPreviewSlide: 0,
      showProjectDropdown: false,
      projectSearchQuery: '',
    };
  },
  watch: {
    currentTab(newTab) {
      localStorage.setItem("workflow_tabs", newTab);
      localStorage.removeItem(`page_workflows_${newTab}`);
    },
    projectSearchQuery(newQuery) {
      this.debouncedSearchProjects(newQuery);
    },
  },
  computed: {
    isSystemAdmin() {
      return this.$store.getters.get_user_role === 'SystemAdmin';
    },
    canImport() {
      return this.selectedProjectId && this.parsedWorkflow && !this.validationError && !this.importing;
    },
    serviceGroups() {
      if (!this.parsedWorkflow?.services?.length) return [];
      const groups = [];
      const services = this.parsedWorkflow.services;
      for (let i = 0; i < services.length; i += 4) {
        groups.push(services.slice(i, i + 4));
      }
      return groups;
    },
    previewSlides() {
      // First slide is workflow info, then service group slides
      const slides = ['workflow'];
      if (this.serviceGroups.length > 0) {
        for (let i = 0; i < this.serviceGroups.length; i++) {
          slides.push(`services_${i}`);
        }
      }
      return slides;
    },
    selectedProjectName() {
      if (!this.selectedProjectId) return null;
      const project = this.projects.find(p => p.id === this.selectedProjectId);
      return project?.project_name || null;
    },
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },
    view_workflow_clients(workflow_id) {
      this.$store.commit('set_workflow_id', workflow_id);
      this.$router.push({
        name: 'admin_view_workflow',
      });
    },
    view_workflow_notifications(workflow_id){
      this.$router.push({ name: 'workflow_notifications', params: { workflow_id } });
    },
    view_workflow_information(workflow_id) {
      this.$router.push({
        name: "view_single_workflow",
        params: { workflow_id },
      });
    },
    get_workflows(page = 1, search = "") {
      const TOKEN = localStorage.getItem("token");
      const status = STATUS_MAP[this.currentTab];
      return new Promise((resolve, reject) => {
        let url = `${API_BASE_URL}/verify_api/verify_admin_api/get_workflows/?page=${page}&status=${status}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: function (data) {
            resolve({
              count: data.count,
              results: data.results,
            });
          },
          error: function (xhr, status, error) {
            reject(error);
          },
        });
      });
    },
    
    openImportModal() {
      this.resetImportForm();
      this.fetchProjects();
    },
    
    resetImportForm() {
      this.selectedProjectId = null;
      this.jsonText = '';
      this.parsedWorkflow = null;
      this.validationError = '';
      this.importing = false;
      this.importSuccess = '';
      this.servicesNotFound = [];
      this.isDragging = false;
      this.uploadedFile = null;
      this.uploadProgress = 100;
      this.currentPreviewSlide = 0;
      this.showProjectDropdown = false;
      this.projectSearchQuery = '';
      if (this.$refs.jsonFileInput) {
        this.$refs.jsonFileInput.value = '';
      }
    },
    
    toggleProjectDropdown() {
      this.showProjectDropdown = !this.showProjectDropdown;
      if (this.showProjectDropdown) {
        this.$nextTick(() => {
          if (this.$refs.projectSearchInput) {
            this.$refs.projectSearchInput.focus();
          }
        });
      }
    },
    
    selectProject(project) {
      this.selectedProjectId = project.id;
      this.showProjectDropdown = false;
      this.projectSearchQuery = '';
    },
    
    fetchProjects(searchQuery = '') {
      const TOKEN = localStorage.getItem("token");
      this.loadingProjects = true;
      
      let url = `${API_BASE_URL}/verify_api/workflow_management_api/get_all_projects_api/`;
      if (searchQuery.trim()) {
        url += `?q=${encodeURIComponent(searchQuery.trim())}`;
      }
      
      $.ajax({
        url: url,
        method: "GET",
        dataType: "json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (data) => {
          if (Array.isArray(data)) {
            this.projects = data;
          } else {
            this.projects = data.results || [];
          }
          this.loadingProjects = false;
        },
        error: () => {
          this.loadingProjects = false;
          const { showError } = useToast();
          showError('Error', 'Failed to load projects');
        },
      });
    },
    
    debouncedSearchProjects: (function() {
      let timeoutId = null;
      return function(query) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          this.fetchProjects(query);
        }, 300);
      };
    })(),
    
    parseJsonFile(event) {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      
      this.uploadedFile = file;
      this.uploadProgress = 0;
      this.currentPreviewSlide = 0;
      
      const progressInterval = setInterval(() => {
        if (this.uploadProgress < 100) {
          this.uploadProgress += 20;
        } else {
          clearInterval(progressInterval);
        }
      }, 100);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.jsonText = e.target.result;
        this.parseJsonText();
      };
      reader.onerror = () => {
        this.validationError = 'Failed to read file';
        this.parsedWorkflow = null;
        this.uploadedFile = null;
      };
      reader.readAsText(file);
    },
    
    handleDragEnter(e) {
      e.preventDefault();
      this.isDragging = true;
    },
    
    handleDragOver(e) {
      e.preventDefault();
      this.isDragging = true;
    },
    
    handleDragLeave(e) {
      e.preventDefault();
      this.isDragging = false;
    },
    
    handleDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type === 'application/json' || file.name.endsWith('.json')) {
          // Create a synthetic event to reuse parseJsonFile
          this.uploadedFile = file;
          this.uploadProgress = 0;
          this.currentPreviewSlide = 0;
          
          // Simulate upload progress
          const progressInterval = setInterval(() => {
            if (this.uploadProgress < 100) {
              this.uploadProgress += 20;
            } else {
              clearInterval(progressInterval);
            }
          }, 100);
          
          const reader = new FileReader();
          reader.onload = (evt) => {
            this.jsonText = evt.target.result;
            this.parseJsonText();
          };
          reader.onerror = () => {
            this.validationError = 'Failed to read file';
            this.parsedWorkflow = null;
            this.uploadedFile = null;
          };
          reader.readAsText(file);
        } else {
          this.validationError = 'Please upload a valid JSON file';
        }
      }
    },
    
    removeUploadedFile() {
      this.uploadedFile = null;
      this.jsonText = '';
      this.parsedWorkflow = null;
      this.validationError = '';
      this.uploadProgress = 100;
      this.currentPreviewSlide = 0;
      if (this.$refs.jsonFileInput) {
        this.$refs.jsonFileInput.value = '';
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    // Carousel navigation methods
    prevPreviewSlide() {
      if (this.currentPreviewSlide > 0) {
        this.currentPreviewSlide--;
      }
    },
    
    nextPreviewSlide() {
      if (this.currentPreviewSlide < this.previewSlides.length - 1) {
        this.currentPreviewSlide++;
      }
    },
    
    goToPreviewSlide(index) {
      this.currentPreviewSlide = index;
    },
    
    downloadTemplate() {
      const template = {
        workflow_name: "Example Workflow",
        workflow_description: "This is a sample workflow template",
        workflow_type: "standard",
        services: [
          { service_name: "Service 1", order: 1 },
          { service_name: "Service 2", order: 2 }
        ]
      };
      
      const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'workflow_template.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    
    parseJsonText() {
      this.validationError = '';
      this.parsedWorkflow = null;
      this.importSuccess = '';
      this.servicesNotFound = [];
      
      if (!this.jsonText.trim()) {
        return;
      }
      
      try {
        const data = JSON.parse(this.jsonText);
        
        // Validate required fields
        if (!data.workflow_name || typeof data.workflow_name !== 'string' || !data.workflow_name.trim()) {
          this.validationError = 'workflow_name is required and must be a non-empty string';
          return;
        }
        
        if (!data.workflow_description || typeof data.workflow_description !== 'string' || !data.workflow_description.trim()) {
          this.validationError = 'workflow_description is required and must be a non-empty string';
          return;
        }
        
        // Validate services array if present
        if (data.services && !Array.isArray(data.services)) {
          this.validationError = 'services must be an array';
          return;
        }
        
        // Validate each service has required fields
        if (data.services && data.services.length > 0) {
          for (let i = 0; i < data.services.length; i++) {
            const service = data.services[i];
            if (!service.service_name) {
              this.validationError = `Service at index ${i} is missing service_name`;
              return;
            }
          }
        }
        
        this.parsedWorkflow = data;
      } catch (e) {
        this.validationError = 'Invalid JSON format: ' + e.message;
      }
    },
    
    importWorkflow() {
      if (!this.canImport) {
        return;
      }
      
      const TOKEN = localStorage.getItem("token");
      this.importing = true;
      this.validationError = '';
      this.importSuccess = '';
      this.servicesNotFound = [];
      
      $.ajax({
        url: `${API_BASE_URL}/verify_api/workflow_management_api/import_workflow/${this.selectedProjectId}/`,
        method: "POST",
        contentType: "application/json",
        dataType: "json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(this.parsedWorkflow),
        success: (response) => {
          this.importing = false;
          const { showSuccess, showWarning } = useToast();
          
          // Show success message
          this.importSuccess = response.message || 'Workflow imported successfully';
          showSuccess('Success', this.importSuccess);
          
          // Handle services not found warning
          if (response.services_not_found && response.services_not_found.length > 0) {
            this.servicesNotFound = response.services_not_found;
            showWarning(
              'Some services skipped',
              `The following services were not found: ${response.services_not_found.join(', ')}`
            );
          }
          
          // Refresh the workflows list
          this.workflowsTableKey++;
          
          // Close modal after a short delay to show success
          setTimeout(() => {
            $('#import_workflow_modal').modal('hide');
            this.resetImportForm();
          }, 1500);
        },
        error: (xhr) => {
          this.importing = false;
          const { showError } = useToast();
          
          let errorMessage = 'Import failed. Please try again.';
          
          if (xhr.responseJSON) {
            errorMessage = xhr.responseJSON.message || xhr.responseJSON.error || errorMessage;
          } else if (xhr.status === 0) {
            errorMessage = 'Network error. Please check your connection.';
          } else if (xhr.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
          } else if (xhr.status === 403) {
            errorMessage = 'You do not have permission to import workflows.';
          } else if (xhr.status === 404) {
            errorMessage = 'Import endpoint not found. Please contact support.';
          }
          
          this.validationError = errorMessage;
          showError('Import Failed', errorMessage);
        },
      });
    },
  },
  mounted() {
    if (!validateToken(this.$router)) {
      this.$router.push({ name: "user_login" });
      return;
    }
    this.get_workflows();
  },
};
